import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Users, Send, TrendingUp, Plus, FileText } from 'lucide-react';
import emailCampaignService from '../../services/emailCampaignService';

const EmailCampaignDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [stats, campaigns] = await Promise.all([
        emailCampaignService.getStatistics(),
        emailCampaignService.getCampaigns({ limit: 5 })
      ]);
      
      setStatistics(stats);
      setRecentCampaigns(Array.isArray(campaigns) ? campaigns : campaigns.results || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-green-100 text-green-800',
      paused: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-4 btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Helmet>
        <title>Email Campaign Dashboard - Admin | NovialCraft</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Email Campaign Dashboard</h1>
          <p className="mt-2 text-gray-600">Manage email templates and campaigns</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link
            to="/admin/email-campaigns/campaigns/new"
            className="bg-primary-600 hover:bg-primary-700 p-6 rounded-lg shadow-md transition-colors duration-200 flex items-center space-x-4"
          >
            <Plus className="h-8 w-8" />
            <div>
              <h3 className="font-semibold text-lg">New Campaign</h3>
              <p className="text-sm text-primary-100">Create email campaign</p>
            </div>
          </Link>

          <Link
            to="/admin/email-campaigns/templates"
            className="admin-card-link block bg-white hover:bg-gray-50 border-2 border-gray-200 p-6 rounded-lg shadow-md transition-colors duration-200"
          >
            <div className="flex items-center space-x-4">
              <FileText className="h-8 w-8 text-gray-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Templates</h3>
                <p className="text-sm">Manage email templates</p>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/email-campaigns/campaigns"
            className="admin-card-link block bg-white hover:bg-gray-50 border-2 border-gray-200 p-6 rounded-lg shadow-md transition-colors duration-200"
          >
            <div className="flex items-center space-x-4">
              <Mail className="h-8 w-8 text-gray-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg">All Campaigns</h3>
                <p className="text-sm">View all campaigns</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Campaigns</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {statistics.overall?.total_campaigns || 0}
                  </p>
                </div>
                <Mail className="h-12 w-12 text-primary-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Emails Sent</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">
                    {statistics.overall?.total_emails_sent || 0}
                  </p>
                </div>
                <Send className="h-12 w-12 text-green-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Failed Emails</p>
                  <p className="text-3xl font-bold text-red-600 mt-2">
                    {statistics.overall?.total_emails_failed || 0}
                  </p>
                </div>
                <TrendingUp className="h-12 w-12 text-red-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Templates</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">
                    {statistics.total_templates || 0}
                  </p>
                </div>
                <FileText className="h-12 w-12 text-blue-600" />
              </div>
            </div>
          </div>
        )}

        {/* Campaign Status Breakdown */}
        {statistics?.by_status && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Campaigns by Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(statistics.by_status).map(([status, count]) => (
                <div key={status} className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600 capitalize">{status}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Campaigns */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Recent Campaigns</h2>
            <Link
              to="/admin/email-campaigns/campaigns"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View All →
            </Link>
          </div>

          <div className="divide-y divide-gray-200">
            {recentCampaigns.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No campaigns yet</p>
                <Link
                  to="/admin/email-campaigns/campaigns/new"
                  className="mt-4 inline-block btn-primary"
                >
                  Create First Campaign
                </Link>
              </div>
            ) : (
              recentCampaigns.map((campaign) => (
                <Link
                  key={campaign.id}
                  to={`/admin/email-campaigns/campaigns/${campaign.id}`}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200 block"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{campaign.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Template: {campaign.template_name}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                        <span>
                          <Users className="inline h-4 w-4 mr-1" />
                          {campaign.total_recipients} recipients
                        </span>
                        <span>
                          <Send className="inline h-4 w-4 mr-1" />
                          {campaign.emails_sent} sent
                        </span>
                        {campaign.emails_failed > 0 && (
                          <span className="text-red-600">
                            {campaign.emails_failed} failed
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {campaign.success_rate !== undefined && campaign.emails_sent > 0 && (
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Success Rate</p>
                          <p className="text-lg font-semibold text-green-600">
                            {campaign.success_rate.toFixed(1)}%
                          </p>
                        </div>
                      )}
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignDashboard;
